import { components, useApi } from "@appretio/api";
import { useMutation } from "@tanstack/react-query";

type AlgorithmicForecastInput =
  components["schemas"]["AlgorithmicForecastInput"];
type AlgorithmicForecastOutput =
  components["schemas"]["AlgorithmicForecastOutput"];
type AlgorithmicModel = components["schemas"]["AlgorithmicModel"];

/** The generate request with its `model` widened to one request per model. */
export type GenerateAlgorithmicForecastInput = Omit<
  AlgorithmicForecastInput,
  "model"
> & {
  models: AlgorithmicModel[];
};

export interface GenerateAlgorithmicForecastResult {
  /** One response per model that answered, in `models` order. */
  outputs: { model: AlgorithmicModel; output: AlgorithmicForecastOutput }[];
  /** Models whose request failed: a 5xx, a dropped connection, a malformed body. */
  failed: { model: AlgorithmicModel; error: unknown }[];
}

/**
 * Mutation hook for `POST /v2/forecast/algorithmic/generate`, one request per
 * selected model — the endpoint fits one model per call and each result is
 * its own `algo:<model>` producer, so the outputs stay separate. A request
 * that fails while another answers is reported per model, so the planner
 * keeps the forecasts that did come back. The mutation rejects only when
 * every request fails: `model` is the only input that differs between them,
 * so a 4xx is about the shared inputs and hits every model alike. Nothing
 * caches this data, so there's no `onSuccess` invalidation. No
 * `meta.errorMessage` either — same reason as `useCreateCube`: the calling
 * dialog owns a more specific error toast (including the per-KPI
 * partial-failure case), and a generic one here would duplicate it.
 */
export function useGenerateAlgorithmicForecast() {
  const apiClient = useApi();

  return useMutation({
    mutationFn: async ({
      models,
      ...input
    }: GenerateAlgorithmicForecastInput): Promise<GenerateAlgorithmicForecastResult> => {
      const settled = await Promise.allSettled(
        models.map(async (model) => {
          const response = await apiClient.POST(
            "/v2/forecast/algorithmic/generate",
            { body: { ...input, model } }
          );
          if (!response.data?.result) {
            throw new Error(
              "Missing result in algorithmic forecast generate response"
            );
          }
          return { model, output: response.data.result };
        })
      );
      const result: GenerateAlgorithmicForecastResult = {
        outputs: [],
        failed: []
      };
      settled.forEach((outcome, index) => {
        if (outcome.status === "fulfilled") {
          result.outputs.push(outcome.value);
        } else {
          result.failed.push({ model: models[index], error: outcome.reason });
        }
      });
      if (result.outputs.length === 0 && result.failed.length > 0) {
        throw result.failed[0].error;
      }
      return result;
    }
  });
}
